import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, FileCheck2, MapPin, Building2, CalendarCheck, Camera } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Seo } from "../components/site/Seo";
import { Reveal, Eyebrow } from "../components/site/Reveal";
import { CertificateRequestDialog } from "../components/site/CertificateRequestDialog";
import { PROJECTS } from "../data/projects";
import { WORKS } from "../data/site";
import { useProjectPhotos } from "../lib/photos";
import { BrochureButton } from "../components/site/BrochureButton";

const Fact = ({ icon: Icon, label, value }) => (
  <div className="border-t border-white/15 py-4">
    <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-white/50"><Icon className="h-4 w-4" /> {label}</p>
    <p className="mt-1 font-semibold text-white">{value}</p>
  </div>
);

const ItemsTable = ({ items }) => (
  <table data-testid="project-items-table" className="w-full text-left">
    <thead>
      <tr className="border-b-2 border-[var(--ink)] font-mono text-xs uppercase tracking-wider text-[var(--muted-ink)]">
        <th className="py-3 pr-4">Item</th><th className="py-3 pr-4 text-right">Quantity</th><th className="py-3">Unit</th>
      </tr>
    </thead>
    <tbody>
      {items.map(([d, q, u]) => (
        <tr key={d} className="border-b border-black/10">
          <td className="py-3 pr-4">{d}</td><td className="py-3 pr-4 text-right font-mono">{q}</td><td className="py-3 font-mono text-sm text-[var(--muted-ink)]">{u}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

export default function ProjectPage() {
  const { slug } = useParams();
  const project = PROJECTS.find((p) => p.slug === slug);
  const [active, setActive] = useState(null);
  const [certWork, setCertWork] = useState(null);
  const { data: uploaded = [] } = useProjectPhotos(slug);
  if (!project) return <Navigate to="/" replace />;
  const images = [...project.images, ...uploaded];
  const cover = images[0];
  const work = WORKS.find((w) => w.id === project.workId);
  const others = PROJECTS.filter((p) => p.slug !== slug);

  return (
    <main data-testid="project-page">
      <Seo title={project.short} path={`/projects/${slug}`} image={cover?.src.startsWith("/") ? cover.src : "/img/hero.jpg"}
        description={`${project.title}. ${project.client}, ${project.division}. ${project.highlights.join(", ")}. Completed by Vata Infrastructures, KPWD Class-I civil contractor, Karnataka.`} />
      <section className="relative overflow-hidden bg-[var(--forest)] text-white">
        {cover && <img src={cover.src} alt={cover.alt} className="absolute inset-0 h-full w-full object-cover opacity-35" />}
        <div className="absolute inset-0 bg-gradient-to-r from-[#141c17] via-[#141c17]/85 to-[#141c17]/40" />
        <div className="relative mx-auto grid max-w-7xl gap-12 px-5 pb-16 pt-32 md:px-10 md:pt-40 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <Link to="/#projects" data-testid="project-back-link" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-white/70 hover:text-white"><ArrowLeft className="h-4 w-4" /> All projects</Link>
            <p className="eyebrow mt-8 text-[#E9C7B9]">{project.category} · {project.year}</p>
            <h1 data-testid="project-title" className="mt-4 font-display text-5xl font-extrabold uppercase leading-[0.92] sm:text-6xl">{project.title}</h1>
            <p className="mt-6 max-w-2xl text-white/80">{project.summary}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {project.highlights.map((h) => <span key={h} className="border border-white/25 px-3 py-1.5 font-mono text-xs">{h}</span>)}
            </div>
          </div>
          <div className="self-end lg:col-span-4" data-testid="project-facts">
            <p className="font-display text-5xl font-extrabold">{project.value}</p>
            <p className="mb-6 font-mono text-xs uppercase tracking-wider text-white/60">{project.valueNote}{project.tendered ? ` · ${project.tendered}` : ""}</p>
            <Fact icon={Building2} label="Client" value={`${project.client} · ${project.division}`} />
            <Fact icon={MapPin} label="Location" value={project.location} />
            <Fact icon={CalendarCheck} label="Completed" value={project.completed} />
          </div>
        </div>
      </section>

      <section className="section bg-[var(--paper)]">
        <div className="mx-auto grid max-w-7xl gap-16 px-5 md:px-10 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <Eyebrow>Quantities</Eyebrow>
            <h2 className="section-title mt-4">Executed items</h2>
            <div className="mt-8"><ItemsTable items={project.items} /></div>
            <p className="mt-4 text-sm text-[var(--muted-ink)]">As stated on the work done certificate issued by the department.</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="bg-[var(--ink)] p-8 text-white">
              <FileCheck2 className="h-8 w-8 text-[var(--rust-light)]" />
              <h3 className="mt-4 font-display text-3xl font-bold uppercase">Need the certificate?</h3>
              <p className="mt-3 text-sm text-white/70">Tender reviewers can request a copy of the work done certificate for this project.</p>
              <button type="button" onClick={() => setCertWork(work)} data-testid="project-request-certificate-button" className="btn-rust mt-6 w-full py-4">Request certificate</button>
              <BrochureButton className="mt-3 w-full justify-center py-4" testid="project-brochure-button" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section bg-[var(--sand)]">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Eyebrow>Site photos</Eyebrow>
          <h2 className="section-title mt-4">On site</h2>
          {images.length === 0 ? (
            <div data-testid="project-photos-empty" className="mt-10 flex items-center gap-4 border border-dashed border-black/25 p-10 text-[var(--muted-ink)]">
              <Camera className="h-8 w-8 shrink-0" /> Site photos for this project are being added. Contact us for photos or a site visit.
            </div>
          ) : (
            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {images.map((img, i) => (
                <button key={img.src} type="button" onClick={() => setActive(img)} data-testid={`project-photo-${i}`} className="group overflow-hidden">
                  <img src={img.src} alt={img.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-[var(--paper)] py-20">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Eyebrow>More projects</Eyebrow>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {others.map((p) => (
              <Link key={p.slug} to={`/projects/${p.slug}`} data-testid={`other-project-${p.slug}`} className="border-t-4 border-[var(--forest)] bg-white p-5 transition-[border-color] hover:border-[var(--rust)]">
                <p className="font-mono text-xs uppercase text-[var(--rust)]">{p.year}</p>
                <p className="mt-2 font-display text-xl font-bold uppercase leading-tight">{p.short}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent data-testid="project-lightbox" className="max-w-5xl border-0 bg-black p-0 [&>button]:rounded-full [&>button]:bg-white [&>button]:p-1 [&>button]:opacity-100">
          <DialogTitle className="sr-only">{active?.alt}</DialogTitle>
          {active && <img src={active.src} alt={active.alt} className="max-h-[85vh] w-full object-contain" />}
        </DialogContent>
      </Dialog>
      <CertificateRequestDialog work={certWork} onClose={() => setCertWork(null)} />
    </main>
  );
}
